import React from 'react';

import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FormControlComponent } from 'design/formControl';
import { PressControlComponent } from 'design/pressControl';
import { validatePersonalInfoFormValues } from 'utils/validatePersonalInfoFormValues';
import { ProdStackManagerPersonalInfoFieldset } from 'containers/ProdStackManager/ProdStackManagerPersonalInfoFieldset';
import { PartnerCodeFieldComponent } from 'design/partnerCodeField';
import { MarginComponent } from 'design/margin';
import { usePersonalInfoFormValues } from 'hooks/usePersonalInfoFormValues';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { PartnerProfile } from 'types/state';
import { useServices } from 'hooks/useServices';
import { PartnerNotFoundError } from 'utils/errors';
import { useSafeAsyncWrapper } from 'hooks/useSafeAsyncWrapper';
import { CodeInputLayoutComponent } from 'design/codeInputLayout';
import { CharInputs } from 'containers/CharInputs';
import { SpinnerComponent } from 'design/spinner';
import { ReactComponent as CheckMarkComponent } from 'svg/check-alternate.svg';
import { ContentLayoutComponent } from 'design/contentLayout';
import { useAppState } from 'hooks/useAppState';
import { useProdStakcManagerOrgInfoForm } from 'hooks/useProdStackManagerOrgInfoForm';
import { ProdStackManagerOrgInfoFieldset } from './ProdStackManagerOrgInfoFieldset';
import { validateOrgInfoFormValues } from 'utils/validateOrgInfoFormValues';

type Props = {
  onSubmitSucceed(): void;
};

export const ProdStackManagerClientInfoForm = (props: Props) => {
  const { onSubmitSucceed } = props;

  const intl = useIntlDictionary();

  const wrapAsyncSafely = useSafeAsyncWrapper();

  const {
    prodStackManager: { clientInfoForm: ownDictionary },
  } = intl;

  const {
    values: personalInfoFormValues,
    violations: personalInfoFormViolations,
    setViolations: setPersonalInfoFormViolations,
    changeFirstName,
    changeLastName,
    changeMiddleName,
    changePartnerCode,
    changePhone,
    changePosition,
  } = usePersonalInfoFormValues();

  const {
    values: orgInfoFormValues,
    violations: orgInfoFormViolations,
    setViolations: setOrgInfoFormViolations,
    changeLocation,
    changeName,
    changeStaffCount,
  } = useProdStakcManagerOrgInfoForm();

  const totalVoilationsLength =
    personalInfoFormViolations.length + orgInfoFormViolations.length;

  const { user } = useAppState();

  React.useEffect(() => {
    if (user) {
      const { partnerProfile } = user;

      changeFirstName(partnerProfile.firstName);
      changeLastName(partnerProfile.lastName);
      changeMiddleName(partnerProfile.middleName);
    }
  }, [changeFirstName, changeLastName, changeMiddleName, user]);

  const [
    partnerCodeVerificationInProgress,
    setPartnerCodeVerificationInProgress,
  ] = React.useState(false);

  const [
    partnerProfile,
    setPartnerProfile,
  ] = React.useState<null | PartnerProfile>(null);

  const { getPartnerByCode } = useServices();

  const joinedPartnerCode = personalInfoFormValues.partnerCode.join('').trim();

  React.useEffect(() => {
    setPartnerProfile(null);
  }, [joinedPartnerCode]); // WARNING: неиспользуемая зависимость

  React.useEffect(() => {
    if (joinedPartnerCode.length === 4) {
      setPartnerCodeVerificationInProgress(true);

      const promise = getPartnerByCode(joinedPartnerCode)
        .then((partner) => {
          setPartnerProfile(partner.profile);
          setPartnerCodeVerificationInProgress(false);
        })
        .catch((error) => {
          if (error instanceof PartnerNotFoundError) {
            changePartnerCode(['', '', '', '']);
            setPersonalInfoFormViolations((prev) => [
              ...prev,
              { name: 'PartnerNotFound' },
            ]);
            setPartnerCodeVerificationInProgress(false);
          } else {
            throw error;
          }
        });

      wrapAsyncSafely(promise);
    }
  }, [
    changePartnerCode,
    getPartnerByCode,
    joinedPartnerCode,
    setPersonalInfoFormViolations,
    wrapAsyncSafely,
  ]);

  const submit = React.useCallback(() => {
    setPersonalInfoFormViolations([]);
    setOrgInfoFormViolations([]);

    const nextOrgInfoFormViolations = validateOrgInfoFormValues(
      orgInfoFormValues
    );

    const nextPersonalInfoFormViolations = validatePersonalInfoFormValues(
      personalInfoFormValues
    );

    if (nextOrgInfoFormViolations.length > 0) {
      Promise.resolve(nextOrgInfoFormViolations).then(setOrgInfoFormViolations);
    }

    if (nextPersonalInfoFormViolations.length > 0) {
      Promise.resolve(nextPersonalInfoFormViolations).then(
        setPersonalInfoFormViolations
      );
    }

    const nextViolationsTotalCount =
      nextOrgInfoFormViolations.length + nextPersonalInfoFormViolations.length;

    if (nextViolationsTotalCount === 0) {
      onSubmitSucceed();
    }
  }, [
    setPersonalInfoFormViolations,
    setOrgInfoFormViolations,
    orgInfoFormValues,
    personalInfoFormValues,
    onSubmitSucceed,
  ]);

  const partnerNotFound = personalInfoFormViolations.some(
    (violation) => violation.name === 'PartnerNotFound'
  );

  const partnerCodeInvalidCharIndexes = React.useMemo(() => {
    if (
      personalInfoFormViolations.some(
        (violation) => violation.name === 'InvalidPartnerCode'
      )
    ) {
      return personalInfoFormValues.partnerCode.reduce<number[]>(
        (acc, char, index) => {
          if (char === '') {
            acc.push(index);
          }

          return acc;
        },
        []
      );
    }
  }, [personalInfoFormValues.partnerCode, personalInfoFormViolations]);

  return (
    <FormControlComponent
      shouldFocusFirstInvalidInput={totalVoilationsLength > 0}
      onSubmit={submit}
    >
      <ContentLayoutComponent
        title={
          <TextComponent size="l" family="futura">
            {ownDictionary.titleText}
          </TextComponent>
        }
        body={
          <>
            <FieldsetLayoutComponent>
              <ProdStackManagerPersonalInfoFieldset
                disabled={partnerCodeVerificationInProgress}
                values={personalInfoFormValues}
                violations={personalInfoFormViolations}
                onFirstNameChange={changeFirstName}
                onLastNameChange={changeLastName}
                onMiddleNameChange={changeMiddleName}
                onPhoneChange={changePhone}
                onPositionChange={changePosition}
              />
              <ProdStackManagerOrgInfoFieldset
                disabled={partnerCodeVerificationInProgress}
                values={orgInfoFormValues}
                violations={orgInfoFormViolations}
                onNameChange={changeName}
                onLocationChange={changeLocation}
                onStaffCountChange={changeStaffCount}
              />
            </FieldsetLayoutComponent>
            <MarginComponent top={32} />
            <PartnerCodeFieldComponent
              label={
                partnerNotFound ? (
                  <TextComponent color="critical">
                    {ownDictionary.partnerNotFoundByCodeText}
                  </TextComponent>
                ) : partnerProfile ? (
                  <TextComponent>
                    {ownDictionary.partnerFoundByCodeText(partnerProfile)}
                  </TextComponent>
                ) : (
                  <TextComponent>
                    {ownDictionary.partnerCodeLabel}
                  </TextComponent>
                )
              }
              input={
                <CodeInputLayoutComponent
                  theme="partnerCode"
                  inputs={
                    <CharInputs
                      name="partnerCode"
                      chars={personalInfoFormValues.partnerCode}
                      onCharsChange={changePartnerCode}
                      invalidCharIndexes={partnerCodeInvalidCharIndexes}
                      disabled={partnerCodeVerificationInProgress}
                    />
                  }
                  indicator={
                    <>
                      {partnerCodeVerificationInProgress && (
                        <TextComponent color="accent">
                          <SpinnerComponent />
                        </TextComponent>
                      )}
                      {partnerProfile !== null && (
                        <TextComponent color="accent">
                          <CheckMarkComponent />
                        </TextComponent>
                      )}
                    </>
                  }
                />
              }
            />
            <MarginComponent top={44} />
            <span>
              <PressControlComponent
                disabled={partnerCodeVerificationInProgress}
                theme="primary"
                type="submit"
              >
                {ownDictionary.actionText}
              </PressControlComponent>
            </span>
          </>
        }
        backAction={null}
      />
    </FormControlComponent>
  );
};
